import i18n from 'i18next';
import { z } from 'zod';
import type { AuditEvent, ListAuditEventsParams, Run } from '@/api/generated/model';
import type en from '../../../../messages/en.json';

type TranslationKey = keyof typeof en;

// Not raisable: the contract caps per_page at 100 (packages/contracts/src/common.ts).
export const AUDIT_TRAIL_PAGE_SIZE = 100;

export function toRunAuditTrailParams(runId: Run['id'], page = 1): ListAuditEventsParams {
	const params: ListAuditEventsParams = { subjectType: 'run', subjectId: runId, per_page: AUDIT_TRAIL_PAGE_SIZE };
	// Spelling out page 1 would split the query key from the route loader's prefetch.
	return page === 1 ? params : { ...params, page };
}

export function toAuditTrailRange(page: number, eventsOnPage: number): { from: number; to: number } {
	const from = (page - 1) * AUDIT_TRAIL_PAGE_SIZE + 1;
	return { from, to: from + eventsOnPage - 1 };
}

// Later slices' event types fall back to their raw type string until labeled here.
const auditEventLabelMap: Record<string, TranslationKey> = {
	'run.created': 'runs_event_created',
	'run.blocked': 'runs_event_blocked',
	'run.guards_passed': 'runs_event_guards_passed',
	'run.modified': 'runs_event_modified',
	'run.approved': 'runs_event_approved',
	'run.rejected': 'runs_event_rejected',
	'run.step_completed': 'runs_event_step_completed',
	'run.step_failed': 'runs_event_step_failed',
	'run.completed': 'runs_event_completed',
	'run.retried': 'runs_event_retried',
	'run.duplicate_suppressed': 'runs_event_duplicate_suppressed',
	'run.group_suspended': 'runs_event_group_suspended',
	'run.group_suspend_cleared': 'runs_event_group_suspend_cleared',
	'run.approver_notified': 'runs_event_approver_notified',
	'run.approver_notification_failed': 'runs_event_approver_notification_failed',
	'participant.join_requested': 'runs_event_participant_join_requested',
	'participant.join_approved': 'runs_event_participant_join_approved',
	'participant.join_rejected': 'runs_event_participant_join_rejected',
	'participant.join_request_revoked': 'runs_event_participant_join_request_revoked',
	'participant.joined_seeded': 'runs_event_participant_joined_seeded',
	'participant.joined': 'runs_event_participant_joined',
	'participant.left': 'runs_event_participant_left',
	'participant.joined_unlisted': 'runs_event_participant_joined_unlisted',
};

export function getAuditEventLabel(type: string): string {
	const key = auditEventLabelMap[type];
	return key ? i18n.t(key) : type;
}

const payloadFieldLabelMap: Record<string, TranslationKey> = {
	customer: 'runs_form_customer_label',
	location: 'runs_form_location_label',
	address: 'runs_form_address_label',
	mapsLink: 'runs_form_maps_link_label',
	processStatus: 'runs_form_process_status_label',
	accessCode: 'runs_form_access_code_label',
	notes: 'runs_form_notes_label',
	participants: 'runs_section_participants_title',
};

export function getPayloadFieldLabel(field: string): string {
	const key = payloadFieldLabelMap[field];
	return key ? i18n.t(key) : field;
}

// Event data is loosely typed on the contract (ADR-0011); the UI narrows per event
// type and renders no detail when the shape is unexpected.
const blockedEventDataSchema = z.object({ reasons: z.array(z.string()) });
const createdEventDataSchema = z.object({ triggerSource: z.string() });
const duplicateSuppressedEventDataSchema = z.object({ externalRef: z.string() });
const modifiedEventDataSchema = z.object({ changedFields: z.array(z.string()) });
const rejectedEventDataSchema = z.object({ note: z.string() });
const stepCompletedEventDataSchema = z.object({ step: z.string() });
const stepFailedEventDataSchema = z.object({ step: z.string(), class: z.string(), message: z.string() });
const retriedEventDataSchema = z.object({ step: z.string() });
const approverNotifiedEventDataSchema = z.object({ recipientName: z.string().nullable() });
const approverNotificationFailedEventDataSchema = z.object({
	recipientName: z.string().nullable(),
	class: z.string(),
	message: z.string(),
});
const membershipEventDataSchema = z.object({ name: z.string() });
const unlistedJoinerEventDataSchema = z.object({ waId: z.string() });
// A join-request event names the matched participant, or carries only a wa_id when the
// requester matches nobody (ADR-0016). The trail shows the name when there is one.
const joinRequestEventDataSchema = z.object({ waId: z.string(), name: z.string().optional() });
const JOIN_REQUEST_EVENT_TYPES = new Set([
	'participant.join_requested',
	'participant.join_approved',
	'participant.join_rejected',
	'participant.join_request_revoked',
]);

export type StepFailureDetail = z.infer<typeof stepFailedEventDataSchema>;

export type ApproverNotificationFailureDetail = z.infer<typeof approverNotificationFailedEventDataSchema>;

// Which trigger produced the run — the timeline's actor line renders the source label
// for machine-created run.created entries (PRD Slice 7 source-label rule).
export function getCreatedTriggerSource(event: AuditEvent): string | null {
	if (event.type !== 'run.created') {
		return null;
	}
	const parsed = createdEventDataSchema.safeParse(event.data);
	return parsed.success ? parsed.data.triggerSource : null;
}

// The re-delivered identity a suppression points at (PRD Slice 7).
export function getSuppressedDeliveryRef(event: AuditEvent): string | null {
	if (event.type !== 'run.duplicate_suppressed') {
		return null;
	}
	const parsed = duplicateSuppressedEventDataSchema.safeParse(event.data);
	return parsed.success ? parsed.data.externalRef : null;
}

export function getBlockedReasons(event: AuditEvent): string[] {
	if (event.type !== 'run.blocked') {
		return [];
	}
	const parsed = blockedEventDataSchema.safeParse(event.data);
	return parsed.success ? parsed.data.reasons : [];
}

export function getChangedFields(event: AuditEvent): string[] {
	if (event.type !== 'run.modified') {
		return [];
	}
	const parsed = modifiedEventDataSchema.safeParse(event.data);
	return parsed.success ? parsed.data.changedFields : [];
}

export function getRejectionNote(event: AuditEvent): string | null {
	if (event.type !== 'run.rejected') {
		return null;
	}
	const parsed = rejectedEventDataSchema.safeParse(event.data);
	return parsed.success ? parsed.data.note : null;
}

export function getCompletedStep(event: AuditEvent): string | null {
	if (event.type !== 'run.step_completed') {
		return null;
	}
	const parsed = stepCompletedEventDataSchema.safeParse(event.data);
	return parsed.success ? parsed.data.step : null;
}

export function getStepFailure(event: AuditEvent): StepFailureDetail | null {
	if (event.type !== 'run.step_failed') {
		return null;
	}
	const parsed = stepFailedEventDataSchema.safeParse(event.data);
	return parsed.success ? parsed.data : null;
}

// Who was told a run is waiting (PRD Slice 6) — the name snapshotted at send time.
// Null when the row was already gone or the data shape is unexpected.
export function getApproverNotifiedRecipient(event: AuditEvent): string | null {
	if (event.type !== 'run.approver_notified') {
		return null;
	}
	const parsed = approverNotifiedEventDataSchema.safeParse(event.data);
	return parsed.success ? parsed.data.recipientName : null;
}

// The best-effort delivery's failure record (PRD Slice 6, taxonomy per ADR-0010) —
// delivery problems are discovered in the panel, not by silence.
export function getApproverNotificationFailure(event: AuditEvent): ApproverNotificationFailureDetail | null {
	if (event.type !== 'run.approver_notification_failed') {
		return null;
	}
	const parsed = approverNotificationFailedEventDataSchema.safeParse(event.data);
	return parsed.success ? parsed.data : null;
}

// The step a retry resumed at (ADR-0014) — recorded on the run.retried event.
export function getRetriedStep(event: AuditEvent): string | null {
	if (event.type !== 'run.retried') {
		return null;
	}
	const parsed = retriedEventDataSchema.safeParse(event.data);
	return parsed.success ? parsed.data.step : null;
}

// Who the membership event is about — the name snapshotted by the processor or
// born-joined seeding (PRD Slice 5, ADR-0021), so the trail needs no row lookup.
export function getMembershipParticipantName(event: AuditEvent): string | null {
	if (
		event.type !== 'participant.joined_seeded' &&
		event.type !== 'participant.joined' &&
		event.type !== 'participant.left'
	) {
		return null;
	}
	const parsed = membershipEventDataSchema.safeParse(event.data);
	return parsed.success ? parsed.data.name : null;
}

// The WhatsApp id an unlisted joiner is known by — the only identity there is.
export function getUnlistedJoinerWaId(event: AuditEvent): string | null {
	if (event.type !== 'participant.joined_unlisted') {
		return null;
	}
	const parsed = unlistedJoinerEventDataSchema.safeParse(event.data);
	return parsed.success ? parsed.data.waId : null;
}

// Who a join-request event (requested / approved / revoked) is about: the snapshotted
// participant name for a matched requester, otherwise the bare WhatsApp id of an unknown
// one. Null when the type isn't a join-request event or the data shape is unexpected.
export function getJoinRequestSubject(event: AuditEvent): { name: string | null; waId: string } | null {
	if (!JOIN_REQUEST_EVENT_TYPES.has(event.type)) {
		return null;
	}
	const parsed = joinRequestEventDataSchema.safeParse(event.data);
	return parsed.success ? { name: parsed.data.name ?? null, waId: parsed.data.waId } : null;
}
